import apiClient from '../api/apiClient';

/**
 * Product Management Service
 * Handles all API calls for Products (Pre-IPO Listings)
 */
class ProductService {
  /**
   * Get products with server-side pagination
   * @param {Object} params - { page, limit, search, status, type, sort_by, sort_order }
   * @returns {Promise<{success: boolean, data: { products, pagination }}>}
   */
  async getProducts(params = {}) {
    const { signal, ...queryParams } = params;
    const response = await apiClient.get('/api/v1/admin/products', { params: queryParams, signal });
    return response.data;
  }

  /**
   * Get single product by ID
   * @param {string|number} productId
   * @returns {Promise<{success: boolean, data: Object}>}
   */
  async getProduct(productId) {
    const response = await apiClient.get(`/api/v1/admin/products/${productId}`);
    return response.data;
  }

  /**
   * Get product attributes (mapped attributes for a product)
   * @param {string|number} productId
   * @returns {Promise<{success: boolean, data: { product_id, attributes: Array, total }}>}
   */
  async getProductAttributes(productId) {
    const response = await apiClient.get(`/api/v1/admin/products/${productId}/attributes`);
    const data = response.data;
    const payload = data?.data ?? data;
    const list = payload?.attributes ?? [];
    return {
      success: data?.success !== false,
      data: {
        product_id: payload?.product_id ?? productId,
        attributes: Array.isArray(list) ? list : [],
        total: payload?.total ?? (Array.isArray(list) ? list.length : 0),
      },
      message: data?.message,
    };
  }

  /**
   * Create a new product
   * @param {Object} productData
   * @returns {Promise<{success: boolean, message: string, data: Object}>}
   */
  async createProduct(productData) {
    const response = await apiClient.post('/api/v1/admin/products', productData);
    return response.data;
  }

  /**
   * Update a product
   * @param {string|number} productId
   * @param {Object} productData
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async updateProduct(productId, productData) {
    const response = await apiClient.patch(`/api/v1/admin/products/${productId}`, productData);
    return response.data;
  }

  /**
   * Update product preference/order (single product; for create/edit flows)
   * @param {string|number} productId
   * @param {number} preferenceIndex
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async updateProductOrder(productId, preferenceIndex) {
    const response = await apiClient.patch(`/api/v1/admin/products/${productId}`, {
      preference_index: preferenceIndex,
    });
    return response.data;
  }

  /**
   * Reorder products in one request (drag & drop or up/down).
   * Sends the full list of product UUIDs in the desired order.
   * @param {string[]} uuids - Array of product UUIDs in the exact display order (first = first in list)
   * @returns {Promise<{success: boolean, message?: string}>}
   */
  async reorderProducts(uuids) {
    const response = await apiClient.put('/api/v1/admin/products/reorder', { uuids });
    return response.data;
  }

  /**
   * Get all industries (for dropdown)
   * @returns {Promise<{success: boolean, data: Array<{id, name}>}>}
   */
  async getIndustries() {
    const response = await apiClient.get('/api/v1/admin/industries');
    return {
      success: true,
      data: response.data?.message || response.data?.data || [],
    };
  }

  /**
   * Get product types (for dropdown) - if available
   * @returns {Promise<{success: boolean, data: Array}>}
   */
  async getProductTypes() {
    try {
      const response = await apiClient.get('/api/v1/admin/product-types');
      return response.data;
    } catch {
      return {
        success: true,
        data: [{ id: '1', name: 'Pre-IPO' }],
      };
    }
  }
}

const productService = new ProductService();
export default productService;
